import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Download, FileSpreadsheet } from "lucide-react";

const downloadFile = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};


const JENIS = [
  { v: "kas", l: "Kas & Setara Kas" },
  { v: "piutang", l: "Piutang" },
  { v: "investasi", l: "Investasi (saham/reksadana/emas)" },
  { v: "kendaraan", l: "Kendaraan" },
  { v: "properti", l: "Tanah & Bangunan" },
  { v: "lainnya", l: "Harta Lainnya" },
];
const jl = (v) => JENIS.find((x) => x.v === v)?.l || v || "-";

export default function HartaPage() {
  const nowY = new Date().getFullYear();
  const [tahun, setTahun] = useState(nowY);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { tahun: nowY, jenis: "kas", nama: "", tahun_perolehan: nowY, nilai: 0, keterangan: "" };
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/harta", { params: { tahun } }); setItems(r.data); };
  useEffect(() => { load(); }, [tahun]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm({ ...empty, tahun }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.nama?.trim()) { toast.error("Nama harta wajib diisi"); return; }
    const payload = { ...form, tahun: Number(form.tahun) || tahun, tahun_perolehan: Number(form.tahun_perolehan) || null, nilai: Number(form.nilai) || 0 };
    try {
      if (editId) await api.put(`/harta/${editId}`, payload);
      else await api.post("/harta", payload);
      toast.success(editId ? "Diperbarui" : "Harta tersimpan"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delOne = async (id) => { if (!confirm("Hapus harta ini?")) return; await api.delete(`/harta/${id}`); toast.success("Terhapus"); load(); };
  const totalNilai = items.reduce((s, x) => s + Number(x.nilai || 0), 0);

  return (
    <>
      <PageHeader title="Daftar Harta" subtitle="Daftar harta akhir tahun untuk lampiran SPT 1770." actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadFile("/export/harta", { fmt: "pdf", tahun }, `lampiran-harta-${tahun}.pdf`)} className="rounded-sm" data-testid="harta-pdf"><Download size={14} className="mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => downloadFile("/export/harta", { fmt: "xlsx", tahun }, `lampiran-harta-${tahun}.xlsx`)} className="rounded-sm" data-testid="harta-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
          <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-harta-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
        </div>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="card-flat p-4"><Label>Tahun Pajak</Label><Input type="number" className="w-32" value={tahun} onChange={(e) => setTahun(Number(e.target.value) || tahun)} data-testid="harta-tahun" /></div>
          <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Harta {tahun}</div><div className="mt-1 font-mono text-2xl text-slate-900">{rupiah(totalNilai)}</div></div>
        </div>
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="harta-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Jenis</th><th className="p-3">Nama Harta</th><th className="p-3">Th. Perolehan</th><th className="p-3 text-right">Nilai</th><th className="p-3 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-slate-400">Belum ada harta.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">{jl(p.jenis)}</td>
                  <td className="p-3">{p.nama}<div className="text-[11px] text-slate-400">{p.keterangan}</div></td>
                  <td className="p-3">{p.tahun_perolehan || "-"}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(p.nilai)}</td>
                  <td className="p-3"><div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-harta-${p.id}`}><Pencil size={14} /></button>
                    <button onClick={() => delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-harta-${p.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="harta-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Harta</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tahun Pajak</Label><Input type="number" value={form.tahun} onChange={upd("tahun")} /></div>
            <div className="space-y-1.5"><Label>Jenis Harta</Label>
              <Select value={form.jenis} onValueChange={(v) => setForm((f) => ({ ...f, jenis: v }))}>
                <SelectTrigger data-testid="harta-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>{JENIS.map((j) => <SelectItem key={j.v} value={j.v}>{j.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nama Harta <span className="text-rose-500">*</span></Label><Input value={form.nama} onChange={upd("nama")} placeholder="mis. Tabungan BCA / Motor Honda" data-testid="harta-nama" /></div>
            <div className="space-y-1.5"><Label>Tahun Perolehan</Label><Input type="number" value={form.tahun_perolehan || ""} onChange={upd("tahun_perolehan")} /></div>
            <div className="space-y-1.5"><Label>Nilai (Rp)</Label><CurrencyInput value={form.nilai} onValueChange={upd("nilai")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="brand-btn rounded-sm" data-testid="harta-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
